import React, { useState, useEffect } from "react";
import api from "../../../../services/api";
import MapPicker from "../../../../components/MapPicker";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";

export default ({ isOpen, toggle, clienteId, empresaId, notify, onUpdate }) => {
  const [imoveis, setImoveis] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingImovel, setEditingImovel] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showCultivoModal, setShowCultivoModal] = useState(false);
  const [selectedImovelForCultivo, setSelectedImovelForCultivo] =
    useState(null);
  const [cultivosDisponiveis, setCultivosDisponiveis] = useState([]);
  const [selectedCultivo, setSelectedCultivo] = useState("");
  const [addingCultivo, setAddingCultivo] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    latitude: "",
    longitude: "",
    cidade_id: "",
    categoria_imoveis_id: "",
  });

  useEffect(() => {
    if (isOpen) {
      loadCategorias();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && clienteId && empresaId) {
      loadImoveis();
      loadCultivosDisponiveis();
    }
  }, [isOpen, clienteId, empresaId]);

  async function loadImoveis() {
    setLoading(true);
    try {
      const response = await api.get(
        `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis`
      );
      setImoveis(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar imóveis:", error);
      notify("danger", "Erro ao carregar imóveis");
    } finally {
      setLoading(false);
    }
  }

  async function loadCategorias() {
    try {
      const response = await api.get(`/common/categoria-imoveis`);
      setCategorias(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  }

  async function loadCultivosDisponiveis() {
    try {
      const response = await api.get(`/common/cultivos`);
      setCultivosDisponiveis(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar cultivos disponíveis:", error);
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.latitude || !formData.longitude) {
      notify("warning", "Por favor, defina a localização da propriedade");
      return;
    }

    setSaving(true);

    try {
      if (editingImovel) {
        await api.put(
          `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis/${editingImovel.id}`,
          formData
        );
        notify("success", "Imóvel atualizado com sucesso");
      } else {
        await api.post(
          `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis`,
          formData
        );
        notify("success", "Imóvel cadastrado com sucesso");
      }

      resetForm();
      loadImoveis();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao salvar imóvel:", error);
      notify("danger", "Erro ao salvar imóvel");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (imovel) => {
    setEditingImovel(imovel);
    setFormData({
      nome: imovel.nome || "",
      descricao: imovel.descricao || "",
      latitude: imovel.latitude || "",
      longitude: imovel.longitude || "",
      cidade_id: imovel.cidade_id || "",
      categoria_imoveis_id: imovel.categoria_imoveis_id || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (imovel) => {
    if (window.confirm(`Deseja excluir o imóvel "${imovel.nome}"?`)) {
      try {
        await api.delete(
          `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis/${imovel.id}`
        );
        notify("success", "Imóvel excluído com sucesso");
        loadImoveis();
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error("Erro ao excluir imóvel:", error);
        notify("danger", "Erro ao excluir imóvel");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      descricao: "",
      latitude: "",
      longitude: "",
      cidade_id: "",
      categoria_imoveis_id: "",
    });
    setEditingImovel(null);
    setShowForm(false);
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  const handleMapConfirm = (lat, lng) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toFixed(7),
      longitude: lng.toFixed(7),
    }));
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      notify("warning", "Geolocalização não suportada pelo navegador");
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData((prev) => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(7),
          longitude: position.coords.longitude.toFixed(7),
        }));
        setGettingLocation(false);
        notify("success", "Localização capturada com sucesso");
      },
      (error) => {
        setGettingLocation(false);
        console.error("Erro ao obter localização:", error);
        notify(
          "danger",
          "Erro ao obter localização. Verifique as permissões do navegador."
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleClearCoordinates = () => {
    setFormData((prev) => ({
      ...prev,
      latitude: "",
      longitude: "",
    }));
  };

  const handleOpenCultivoModal = (imovel) => {
    setSelectedImovelForCultivo(imovel);
    setSelectedCultivo("");
    setShowCultivoModal(true);
  };

  const handleCloseCultivoModal = () => {
    setShowCultivoModal(false);
    setSelectedImovelForCultivo(null);
    setSelectedCultivo("");
  };

  const handleAdicionarCultivo = async (e) => {
    e.preventDefault();
    if (!selectedCultivo || !selectedImovelForCultivo) return;

    setAddingCultivo(true);
    try {
      await api.post(
        `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis/${selectedImovelForCultivo.id}/cultivos`,
        { cultivo_id: selectedCultivo }
      );

      notify("success", "Cultivo adicionado com sucesso");
      handleCloseCultivoModal();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao adicionar cultivo:", error);
      notify("danger", "Erro ao adicionar cultivo");
    } finally {
      setAddingCultivo(false);
    }
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>
        Propriedades / Empreendimentos
      </ModalHeader>
      <ModalBody>
        {!showForm ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Lista de Propriedades</h6>
              <Button
                color="primary"
                size="sm"
                onClick={() => setShowForm(true)}
              >
                <i className="fas fa-plus mr-1" />
                Adicionar Nova
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : (
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {imoveis.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">
                        Nenhuma propriedade cadastrada
                      </td>
                    </tr>
                  ) : (
                    imoveis.map((imovel) => (
                      <tr key={imovel.id}>
                        <td>
                          <strong>{imovel.nome}</strong>
                        </td>
                        <td>
                          {(imovel.categoria_imovel &&
                            imovel.categoria_imovel.nome) ||
                          (imovel.categoriaImovel &&
                            imovel.categoriaImovel.nome) ? (
                            <Badge color="info" pill>
                              {(imovel.categoria_imovel &&
                                imovel.categoria_imovel.nome) ||
                                (imovel.categoriaImovel &&
                                  imovel.categoriaImovel.nome)}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {imovel.descricao ? (
                            <span>
                              {imovel.descricao.substring(0, 50)}
                              {imovel.descricao.length > 50 ? "..." : ""}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <Button
                            color="success"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleOpenCultivoModal(imovel)}
                            title="Adicionar Cultivo"
                          >
                            <i className="fas fa-seedling" />
                          </Button>
                          <Button
                            color="info"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleEdit(imovel)}
                            title="Editar"
                          >
                            <i className="fas fa-edit" />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDelete(imovel)}
                            title="Excluir"
                          >
                            <i className="fas fa-trash" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="nome">Nome *</Label>
                  <Input
                    type="text"
                    name="nome"
                    id="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Fazenda São João"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="categoria_imoveis_id">Categoria</Label>
                  <Input
                    type="select"
                    name="categoria_imoveis_id"
                    id="categoria_imoveis_id"
                    value={formData.categoria_imoveis_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione uma categoria</option>
                    {categorias.map((categoria) => (
                      <option key={categoria.id} value={categoria.id}>
                        {categoria.nome}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="descricao">Descrição</Label>
              <Input
                type="textarea"
                name="descricao"
                id="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                rows="3"
                placeholder="Descrição da propriedade..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Localização *</Label>
              <div className="d-flex gap-2 mb-2">
                <Button
                  type="button"
                  color="info"
                  size="sm"
                  outline
                  onClick={() => setShowMapPicker(true)}
                  className="mr-2"
                >
                  <i className="fas fa-map-marked-alt mr-1" />
                  Marcar no Mapa
                </Button>
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  outline
                  onClick={handleGetCurrentLocation}
                  disabled={gettingLocation}
                >
                  {gettingLocation ? (
                    <Spinner size="sm" className="mr-1" />
                  ) : (
                    <i className="fas fa-location-arrow mr-1" />
                  )}
                  Usar Localização Atual
                </Button>
              </div>

              {formData.latitude && formData.longitude ? (
                <Alert color="success" className="mb-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <i className="fas fa-check-circle mr-2" />
                      <strong>Coordenadas definidas:</strong>
                      <br />
                      <small>
                        Latitude: {formData.latitude} | Longitude:{" "}
                        {formData.longitude}
                      </small>
                    </div>
                    <Button
                      type="button"
                      color="danger"
                      size="sm"
                      onClick={handleClearCoordinates}
                    >
                      <i className="fas fa-times" />
                    </Button>
                  </div>
                </Alert>
              ) : (
                <Alert color="warning" className="mb-0">
                  <i className="fas fa-exclamation-triangle mr-2" />
                  <small>
                    Nenhuma localização definida. Use uma das opções acima.
                  </small>
                </Alert>
              )}
            </FormGroup>

            <div className="d-flex justify-content-end">
              <Button
                type="button"
                color="secondary"
                className="mr-2"
                onClick={resetForm}
              >
                Cancelar
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving && <Spinner size="sm" className="mr-1" />}
                {editingImovel ? "Atualizar" : "Cadastrar"}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>

      <MapPicker
        isOpen={showMapPicker}
        toggle={() => setShowMapPicker(false)}
        initialCoords={
          formData.latitude && formData.longitude
            ? {
                lat: parseFloat(formData.latitude),
                lng: parseFloat(formData.longitude),
              }
            : null
        }
        onConfirm={handleMapConfirm}
      />

      <Modal isOpen={showCultivoModal} toggle={handleCloseCultivoModal}>
        <ModalHeader toggle={handleCloseCultivoModal}>
          Adicionar Cultivo
        </ModalHeader>
        <Form onSubmit={handleAdicionarCultivo}>
          <ModalBody>
            {selectedImovelForCultivo && (
              <div className="mb-3">
                <small className="text-muted">
                  <i className="fas fa-home mr-1" />
                  Propriedade: <strong>{selectedImovelForCultivo.nome}</strong>
                </small>
              </div>
            )}

            <FormGroup>
              <Label for="cultivo">Selecione o Cultivo *</Label>
              <Input
                type="select"
                name="cultivo"
                id="cultivo"
                value={selectedCultivo}
                onChange={(e) => setSelectedCultivo(e.target.value)}
                required
              >
                <option value="">Selecione um cultivo</option>
                {cultivosDisponiveis.map((cultivo) => (
                  <option key={cultivo.id} value={cultivo.id}>
                    {cultivo.nome}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="secondary"
              onClick={handleCloseCultivoModal}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              color="success"
              disabled={addingCultivo || !selectedCultivo}
            >
              {addingCultivo && <Spinner size="sm" className="mr-1" />}
              <i className="fas fa-seedling mr-1" />
              Adicionar Cultivo
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </Modal>
  );
};
